import React from "react";
import { Plus } from "lucide-react";
import { Button } from "../core/button";
import { useTranslation } from "react-i18next";
import { ListToolbar } from "./list-toolbar/list-toolbar";
import { SearchInput } from "./list-toolbar/search-input";
import { FilterPopover } from "./list-toolbar/filter-popover";
import { ActiveFilterChips } from "./list-toolbar/active-filter-chips";
import { useListFilters, deriveFilterChips, countActiveFilters, type FilterDef, type FilterValue, type FilterValues } from "./list-toolbar/use-list-filters";

interface SearchActionsProps {
  onClickAdd?: () => void;
  onFilterChange: (value: string) => void;
  addButtonText?: string;
  placeholder?: string;
  showAddButton?: boolean;
  showEnabledFilter?: boolean;
  enabledOnly?: boolean;
  onEnabledOnlyChange?: (value: boolean) => void;
  enabledOnlyLabel?: string;
  /** Extra filter CONTROLS next to the Filters button. Prefer `filterDefs` so everything lives in the one popover. */
  filters?: React.ReactNode;
  /** Extra filters for the popover (besides the optional "enabled only" toggle). */
  filterDefs?: FilterDef[];
  filterValues?: FilterValues;
  onFilterValueChange?: (key: string, value: FilterValue) => void;
  onClearFilters?: () => void;
  /** Non-filter buttons (export, pending requests…) shown on the right next to the primary action. */
  extraActions?: React.ReactNode;
  /** Segment tabs rendered above the toolbar. */
  tabs?: React.ReactNode;
}

/**
 * Admin list toolbar: the `ListToolbar` family with the admin strings wired
 * in. The optional "enabled only" switch lives inside the Filters popover and
 * shows as a removable chip while it is on.
 */
export const SearchActions = ({
  onClickAdd,
  onFilterChange,
  addButtonText,
  placeholder,
  showAddButton = true,
  showEnabledFilter = false,
  enabledOnly = false,
  onEnabledOnlyChange,
  enabledOnlyLabel,
  filters,
  filterDefs = [],
  filterValues = {},
  onFilterValueChange,
  onClearFilters,
  extraActions,
  tabs,
}: SearchActionsProps) => {
  const { t } = useTranslation("admin");
  const resolvedAddText = addButtonText ?? t("common.actions.add");
  const resolvedPlaceholder = placeholder ?? t("common.placeholders.search");
  const resolvedEnabledLabel = enabledOnlyLabel ?? t("common.filters.enabledOnly");

  const defs = React.useMemo<FilterDef[]>(
    () => (showEnabledFilter ? [{ key: "enabled", label: resolvedEnabledLabel, type: "toggle" }] : []),
    [showEnabledFilter, resolvedEnabledLabel],
  );
  const list = useListFilters(defs, { enabled: enabledOnly });

  // Keep the parent's boolean in sync with the popover switch.
  React.useEffect(() => {
    if (!showEnabledFilter || !onEnabledOnlyChange) return;
    const next = list.values.enabled === true;
    if (next !== enabledOnly) onEnabledOnlyChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.values.enabled]);

  const allDefs = React.useMemo(() => [...defs, ...filterDefs], [defs, filterDefs]);
  const allValues = React.useMemo(() => ({ ...list.values, ...filterValues }), [list.values, filterValues]);
  const setAny = React.useCallback(
    (key: string, value: FilterValue) => {
      if (defs.some((d) => d.key === key)) list.setValue(key, value);
      else onFilterValueChange?.(key, value);
    },
    [defs, list, onFilterValueChange],
  );
  const clearAll = React.useCallback(() => {
    list.clear();
    onClearFilters?.();
  }, [list, onClearFilters]);
  const chips = React.useMemo(() => deriveFilterChips(allDefs, allValues, setAny), [allDefs, allValues, setAny]);
  const hasPopover = allDefs.length > 0 && (!showEnabledFilter || !!onEnabledOnlyChange);

  return (
    <ListToolbar
      className="mt-4 mx-6"
      tabs={tabs}
      search={
        <SearchInput
          onChange={onFilterChange}
          placeholder={resolvedPlaceholder}
          clearLabel={t("common.actions.clear", { defaultValue: "Clear" })}
        />
      }
      actions={
        hasPopover || filters ? (
          <>
            {hasPopover ? (
              <FilterPopover
                filters={allDefs}
                values={allValues}
                onChange={setAny}
                onClear={clearAll}
                label={t("common.filters.label", { defaultValue: "Filters" })}
                clearLabel={t("common.filters.clear", { defaultValue: "Clear" })}
                allLabel={t("common.filters.all", { defaultValue: "All" })}
                searchPlaceholder={resolvedPlaceholder}
                selectedLabel={t("common.filters.selected", { defaultValue: "{{count}} selected" })}
              />
            ) : null}
            {filters}
          </>
        ) : null
      }
      primaryAction={
        extraActions || (showAddButton && onClickAdd) ? (
          <>
            {extraActions}
            {showAddButton && onClickAdd ? (
              <Button onClick={onClickAdd} variant="outline">
                <Plus className="h-4 w-4" />
                <span className="sr-only sm:not-sr-only">{resolvedAddText}</span>
              </Button>
            ) : null}
          </>
        ) : null
      }
      chips={
        countActiveFilters(allDefs, allValues) > 0 ? (
          <ActiveFilterChips
            chips={chips}
            removeLabel={(l) => t("common.filters.remove", { label: l, defaultValue: `Remove ${l}` })}
          />
        ) : null
      }
    />
  );
};
